import React, { useCallback, useEffect, useMemo, useState } from "react";
import { School, User, BadgeCheck } from "lucide-react";
import {
  confirmResetPassword,
  confirmSignUp,
  resetPassword,
  resendSignUpCode,
  signIn,
  signInWithRedirect,
  signUp,
} from "aws-amplify/auth";
import {
  AuthGoogleSection,
  FEDERATED_PERSONA_OPTIONS,
  useAuthFlowController,
  useFederatedPersona,
  type AuthFlowOperations,
  type ConfirmPasswordResetPayload,
  type FederatedPersona,
  type FederatedSignInPayload,
  type PasswordResetPayload,
  type SignInPayload,
  type SignUpPayload,
  type AccountType,
} from "@triedge/react-components";
import {
  DEV_PERSONA_PASSWORD_KEY,
  TEST_PERSONAS,
} from "../../constants/test-personas";
import { isDevRuntimeFlagEnabled } from "../../config/devRuntimeFlags";
import { PUBLIC_AUTH_CONFIG } from "../../config/publicAuth";
import {
  clearOAuthRedirectState,
  serializeOAuthRedirectState,
  type OAuthRedirectState,
} from "../../auth/oauthRedirectState";
import {
  clearPersonaSignupIntentForEmail,
  persistPersonaSignupIntent,
  type PersonaProvisioningPersona,
  type PersonaSignupAccountType,
} from "../../auth/personaProvisioningStorage";
import {
  resolveParentFacingGoogleOAuthPersona,
  shouldRequestParentFacingGooglePersonaSelection,
} from "../../auth/parentFacingGooglePersona";
import { clearStaleCognitoTokenFamilies } from "../../utils/cognito-profile";
import { useGetSettingsModuleConfigQuery } from "../../state/domainRouterApi";
import { EngineeringSupportBanner } from "../overlays/EngineeringSupportBanner";
import { resolveLocalPersonaSignInPassword } from "./authPageLocalPersona";

export type PublicAuthMode = "signin" | "signup";

type Props = {
  mode: PublicAuthMode;
  onModeChange: (mode: PublicAuthMode) => void;
  onAuthSuccess: () => void;
  enableGoogle: boolean;
  oauthRedirectState: Omit<OAuthRedirectState, "savedAt">;
  initialAccountType?: AccountType | null;
  initialEmail?: string | null;
  inviteSchoolId?: string | null;
  hideHeroPane?: boolean;
  schoolName?: string | null;
  schoolLogoUrl?: string | null;
  googleProvisioningIntent?: {
    email?: string | null;
    requestedPersona?: PersonaProvisioningPersona | null;
    requestedAccountType?: PersonaSignupAccountType | null;
    staffSignupCode?: string | null;
    schoolId?: string | null;
    financialAidInvite?: boolean | null;
  } | null;
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const normalizeStaffSignupCodeInput = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const lastColon = trimmed.lastIndexOf(":");
  const token = lastColon >= 0 ? trimmed.slice(lastColon + 1) : trimmed;
  return token.trim().toUpperCase();
};

const resolveFederatedProvider = (
  provider: FederatedSignInPayload["provider"],
): "Google" => {
  if (provider === "google") {
    return "Google";
  }
  return "Google";
};

const SETTINGS_MODULE_KEY = "staff_settings";

const extractCodeDeliveryDestination = (
  rawOutput: unknown,
): string | undefined => {
  if (!rawOutput || typeof rawOutput !== "object") {
    return undefined;
  }
  const output = rawOutput as {
    destination?: unknown;
    codeDeliveryDetails?: { destination?: unknown };
    nextStep?: { codeDeliveryDetails?: { destination?: unknown } };
  };
  const directDestination =
    typeof output.destination === "string" ? output.destination.trim() : "";
  if (directDestination) {
    return directDestination;
  }
  const nestedDestination =
    typeof output.codeDeliveryDetails?.destination === "string"
      ? output.codeDeliveryDetails.destination.trim()
      : "";
  if (nestedDestination) {
    return nestedDestination;
  }
  const nextStepDestination =
    typeof output.nextStep?.codeDeliveryDetails?.destination === "string"
      ? output.nextStep.codeDeliveryDetails.destination.trim()
      : "";
  return nextStepDestination || undefined;
};

const readSchoolLogoFromSettingsPayload = (rawPayload: unknown): string => {
  let parsedPayload: unknown = rawPayload;

  if (typeof rawPayload === "string") {
    try {
      parsedPayload = JSON.parse(rawPayload);
    } catch {
      return "";
    }
  }

  if (
    !parsedPayload ||
    typeof parsedPayload !== "object" ||
    Array.isArray(parsedPayload)
  ) {
    return "";
  }

  const organization = (parsedPayload as { organization?: unknown })
    .organization;
  if (
    !organization ||
    typeof organization !== "object" ||
    Array.isArray(organization)
  ) {
    return "";
  }

  const logo = (organization as { logo?: unknown }).logo;
  return typeof logo === "string" ? logo.trim() : "";
};

const DEFAULT_LOCAL_PERSONA_EMAILS: Partial<Record<FederatedPersona, string>> =
  {
    parent: TEST_PERSONAS.find((persona) => persona.id === "parent")?.email,
    staff: TEST_PERSONAS.find((persona) => persona.id === "staff")?.email,
    administrator:
      TEST_PERSONAS.find((persona) => persona.id === "owner")?.email ??
      TEST_PERSONAS.find((persona) => persona.id === "staff")?.email,
  };

const resolveLocalPersonaEmail = (persona: FederatedPersona): string => {
  return DEFAULT_LOCAL_PERSONA_EMAILS[persona] ?? "";
};

const clearLegacyPersonaStorage = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem("triedge_persona");
    window.localStorage.removeItem("ystnj_persona");
    window.sessionStorage.removeItem("triedge_persona");
    window.sessionStorage.removeItem("ystnj_persona");
  } catch (error) {
    console.warn("[auth] Unable to clear legacy persona storage:", error);
  }
};

const createAuthOperations = (
  oauthRedirectState: Omit<OAuthRedirectState, "savedAt">,
  googleLoginHint: string | null,
  requestPostAuthPersonaSelection: boolean,
  googlePersona: FederatedPersona | null,
): AuthFlowOperations => ({
  signIn: async (payload: SignInPayload) => {
    const output = await signIn({
      username: payload.email.trim(),
      password: payload.password,
    });
    const signInStep = output.nextStep?.signInStep;
    if (!signInStep || signInStep === "DONE") {
      return { status: "authenticated" };
    }
    return {
      status: "next_step",
      nextStep: signInStep,
    };
  },
  signUp: async (payload: SignUpPayload) => {
    const output = await signUp({
      username: payload.email.trim(),
      password: payload.password,
      options: {
        userAttributes: {
          email: payload.email.trim(),
          given_name: payload.firstName.trim(),
          family_name: payload.lastName.trim(),
        },
      },
    });

    const signUpStep = output.nextStep?.signUpStep;
    if (!signUpStep || signUpStep === "DONE") {
      return {
        status: "complete",
        message: "Account created. You can sign in now.",
      };
    }
    if (signUpStep === "CONFIRM_SIGN_UP") {
      return {
        status: "confirm_sign_up",
        message:
          "Account created. Check your email for a verification code, then sign in.",
      };
    }
    return {
      status: "next_step",
      nextStep: signUpStep,
      message:
        "Account created. Additional verification is required before sign-in.",
    };
  },
  resetPassword: async (payload: PasswordResetPayload) => {
    const output = await resetPassword({
      username: payload.email.trim(),
    });
    return {
      status: "sent",
      destination: output.nextStep?.codeDeliveryDetails?.destination,
    };
  },
  confirmPasswordReset: async (payload: ConfirmPasswordResetPayload) => {
    await confirmResetPassword({
      username: payload.email.trim(),
      confirmationCode: payload.code.trim(),
      newPassword: payload.newPassword,
    });
    return {
      status: "confirmed",
      message: "Password updated. Sign in with your new password.",
    };
  },
  confirmSignUp: async (payload) => {
    await confirmSignUp({
      username: payload.email.trim(),
      confirmationCode: payload.code.trim(),
    });
    return {
      status: "confirmed",
      message: "Account verified. Sign in now.",
    };
  },
  resendSignUpCode: async (payload) => {
    const output = await resendSignUpCode({
      username: payload.email.trim(),
    });
    return {
      status: "resent",
      destination: extractCodeDeliveryDestination(output),
    };
  },
  federatedSignIn: async (payload: FederatedSignInPayload) => {
    try {
      clearStaleCognitoTokenFamilies();
      await signInWithRedirect({
        provider: resolveFederatedProvider(payload.provider),
        customState: serializeOAuthRedirectState({
          ...oauthRedirectState,
          persona: googlePersona,
          postAuthPersonaSelection: requestPostAuthPersonaSelection,
        }),
        options: {
          ...(googleLoginHint ? { loginHint: googleLoginHint } : {}),
          prompt: "SELECT_ACCOUNT",
        },
      });
    } catch (error) {
      clearOAuthRedirectState();
      throw error;
    }
  },
});

export const AuthPage: React.FC<Props> = ({
  mode,
  onModeChange,
  onAuthSuccess,
  enableGoogle,
  oauthRedirectState,
  initialAccountType = null,
  initialEmail = null,
  inviteSchoolId = null,
  hideHeroPane = false,
  schoolName = null,
  schoolLogoUrl = null,
  googleProvisioningIntent = null,
}) => {
  const normalizedInitialEmail = normalizeEmail(initialEmail ?? "");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPasswordResetSupport, setShowPasswordResetSupport] =
    useState(false);
  const [devPersonaPassword, setDevPersonaPassword] = useState("");
  const [devPersonaError, setDevPersonaError] = useState<string | null>(null);
  const allowPersonaTokenMint = PUBLIC_AUTH_CONFIG.localPersonaSigninEnabled;
  const googleLoginHint = PUBLIC_AUTH_CONFIG.googleLoginHint;
  const skipPersonaPassword = useMemo(
    () =>
      PUBLIC_AUTH_CONFIG.localPersonaSkipPasswordEnabled ||
      isDevRuntimeFlagEnabled("skipPersonaPassword"),
    [],
  );
  const configuredPersonaPassword = PUBLIC_AUTH_CONFIG.localPersonaPassword;
  const devDefaultPersonaPassword = "";

  const [signInEmail, setSignInEmail] = useState(normalizedInitialEmail);
  const [signInPassword, setSignInPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState<
    string | null
  >(null);
  const [passwordResetCode, setPasswordResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [pendingPasswordResetEmail, setPendingPasswordResetEmail] = useState<
    string | null
  >(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState(normalizedInitialEmail);
  const [signUpPassword, setSignUpPassword] = useState("");
  const [accountType, setAccountType] = useState<AccountType>(
    initialAccountType === "staff" ? "staff" : "parent",
  );
  const [staffSignupCode, setStaffSignupCode] = useState("");
  // UI-only post-signup state. Set client-side when signup completes for a
  // staff account. Does not require a backend response — purely for UX.
  const [postSignupPersonaType, setPostSignupPersonaType] = useState<
    "parent" | "staff" | null
  >(null);
  const settingsModuleConfigQuery = useGetSettingsModuleConfigQuery({
    moduleKey: SETTINGS_MODULE_KEY,
  });

  const { persona: federatedPersona, setPersona: setFederatedPersona } =
    useFederatedPersona();

  useEffect(() => {
    if (!allowPersonaTokenMint) return;
    try {
      const cached = window.localStorage.getItem(DEV_PERSONA_PASSWORD_KEY);
      if (cached) {
        setDevPersonaPassword(cached);
      }
    } catch (error) {
      console.warn(
        "[auth] Unable to read cached local test persona password:",
        error,
      );
    }
  }, [allowPersonaTokenMint]);

  useEffect(() => {
    if (!allowPersonaTokenMint) return;
    try {
      const next = devPersonaPassword.trim();
      if (next) {
        window.localStorage.setItem(DEV_PERSONA_PASSWORD_KEY, next);
      } else {
        window.localStorage.removeItem(DEV_PERSONA_PASSWORD_KEY);
      }
    } catch (error) {
      console.warn(
        "[auth] Unable to persist local test persona password:",
        error,
      );
    }
  }, [allowPersonaTokenMint, devPersonaPassword]);

  useEffect(() => {
    if (mode !== "signup") return;
    const nextAccountType = initialAccountType === "staff" ? "staff" : "parent";
    setAccountType(nextAccountType);
    setStaffSignupCode("");
    if (allowPersonaTokenMint) {
      setFederatedPersona(nextAccountType === "staff" ? "staff" : "parent");
    }
  }, [allowPersonaTokenMint, initialAccountType, mode, setFederatedPersona]);

  useEffect(() => {
    if (!normalizedInitialEmail) return;
    setSignInEmail(normalizedInitialEmail);
    setSignUpEmail(normalizedInitialEmail);
  }, [normalizedInitialEmail]);

  const googlePersona = useMemo(
    () =>
      resolveParentFacingGoogleOAuthPersona({
        mode,
        hasProvisioningIntent: Boolean(googleProvisioningIntent),
        requestedPersona: googleProvisioningIntent?.requestedPersona,
      }),
    [googleProvisioningIntent, mode],
  );

  const authOperations = useMemo(
    () =>
      createAuthOperations(
        oauthRedirectState,
        googleLoginHint,
        shouldRequestParentFacingGooglePersonaSelection({
          mode,
          hasProvisioningIntent: Boolean(googleProvisioningIntent),
        }),
        googlePersona,
      ),
    [
      googleLoginHint,
      googlePersona,
      googleProvisioningIntent,
      mode,
      oauthRedirectState,
    ],
  );

  const resolveStaffSignupCodeForIntent = useCallback(
    () =>
      accountType === "staff"
        ? normalizeStaffSignupCodeInput(staffSignupCode)
        : "",
    [accountType, staffSignupCode],
  );

  const handlePasswordAuthSuccess = useCallback(() => {
    if (initialAccountType === "staff") {
      persistPersonaSignupIntent({
        email: signInEmail,
        requestedPersona: "staff",
        requestedAccountType: "staff",
        staffSignupCode: resolveStaffSignupCodeForIntent(),
        schoolId: inviteSchoolId,
      });
    }
    onAuthSuccess();
  }, [
    initialAccountType,
    inviteSchoolId,
    onAuthSuccess,
    resolveStaffSignupCodeForIntent,
    signInEmail,
  ]);

  const {
    isSubmitting,
    errorMessage,
    infoMessage,
    clearMessages,
    submitSignIn,
    submitSignUp,
    submitPasswordReset,
    submitConfirmPasswordReset,
    submitConfirmSignUp,
    submitResendSignUpCode,
    submitFederatedSignIn,
  } = useAuthFlowController({
    operations: authOperations,
    onAuthSuccess: handlePasswordAuthSuccess,
  });

  const forgotEmailValid = useMemo(
    () => isValidEmail(signInEmail),
    [signInEmail],
  );
  const resetPasswordsMatch = useMemo(
    () => newPassword.length > 0 && newPassword === confirmNewPassword,
    [confirmNewPassword, newPassword],
  );
  const signInReady = useMemo(
    () => isValidEmail(signInEmail) && signInPassword.trim().length > 0,
    [signInEmail, signInPassword],
  );
  const confirmationReady = useMemo(
    () => isValidEmail(signInEmail) && verificationCode.trim().length > 0,
    [signInEmail, verificationCode],
  );
  const signUpReady = useMemo(
    () =>
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      isValidEmail(signUpEmail) &&
      signUpPassword.trim().length > 0,
    [firstName, lastName, signUpEmail, signUpPassword],
  );
  const selectedLocalPersonaAccountLabel = useMemo(() => {
    if (!federatedPersona) {
      return "";
    }
    return resolveLocalPersonaEmail(federatedPersona);
  }, [federatedPersona]);
  const localPersonaPasswordResolution = useMemo(
    () =>
      resolveLocalPersonaSignInPassword({
        manualPassword: devPersonaPassword,
        configuredPassword: configuredPersonaPassword,
        devDefaultPassword: devDefaultPersonaPassword,
        skipPassword: skipPersonaPassword,
      }),
    [
      configuredPersonaPassword,
      devDefaultPersonaPassword,
      devPersonaPassword,
      skipPersonaPassword,
    ],
  );
  const hasAutomaticLocalPersonaPassword = Boolean(
    configuredPersonaPassword?.trim() || devDefaultPersonaPassword.trim(),
  );
  const shouldShowLocalPersonaPasswordInput =
    !skipPersonaPassword || !hasAutomaticLocalPersonaPassword;
  const settingsSchoolLogoUrl = useMemo(
    () =>
      readSchoolLogoFromSettingsPayload(
        settingsModuleConfigQuery.data?.payload,
      ),
    [settingsModuleConfigQuery.data?.payload],
  );
  const publicSchoolName = useMemo(
    () => String(schoolName ?? "").trim(),
    [schoolName],
  );
  const effectiveSchoolLogoUrl = useMemo(
    () => String(schoolLogoUrl ?? "").trim() || settingsSchoolLogoUrl,
    [schoolLogoUrl, settingsSchoolLogoUrl],
  );
  const hasPendingConfirmation = useMemo(
    () =>
      pendingConfirmationEmail !== null &&
      normalizeEmail(signInEmail) === pendingConfirmationEmail,
    [pendingConfirmationEmail, signInEmail],
  );
  const hasPendingPasswordReset = useMemo(
    () =>
      pendingPasswordResetEmail !== null &&
      normalizeEmail(signInEmail) === pendingPasswordResetEmail,
    [pendingPasswordResetEmail, signInEmail],
  );
  const passwordResetConfirmReady = useMemo(
    () =>
      isValidEmail(signInEmail) &&
      passwordResetCode.trim().length > 0 &&
      resetPasswordsMatch,
    [passwordResetCode, resetPasswordsMatch, signInEmail],
  );

  const clearPendingConfirmation = () => {
    setPendingConfirmationEmail(null);
    setVerificationCode("");
  };

  const clearPendingPasswordReset = () => {
    setPendingPasswordResetEmail(null);
    setPasswordResetCode("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const activatePendingConfirmation = (email: string) => {
    if (!isValidEmail(email)) {
      clearPendingConfirmation();
      return;
    }
    clearPendingPasswordReset();
    setPendingConfirmationEmail(normalizeEmail(email));
    setVerificationCode("");
    setIsForgotPassword(false);
    setShowPasswordResetSupport(false);
    setSignInPassword("");
  };

  const activatePasswordResetRequest = () => {
    clearPendingConfirmation();
    clearPendingPasswordReset();
    setSignInPassword("");
    setIsForgotPassword(true);
    setShowPasswordResetSupport(false);
  };

  const activatePendingPasswordReset = (email: string) => {
    clearPendingConfirmation();
    if (!isValidEmail(email)) {
      clearPendingPasswordReset();
      setIsForgotPassword(true);
      setShowPasswordResetSupport(false);
      return;
    }
    setPendingPasswordResetEmail(normalizeEmail(email));
    setPasswordResetCode("");
    setNewPassword("");
    setConfirmNewPassword("");
    setSignInPassword("");
    setIsForgotPassword(true);
    setShowPasswordResetSupport(false);
  };

  const handleSignInEmailChange = (nextValue: string) => {
    setSignInEmail(nextValue);
    if (
      pendingConfirmationEmail !== null &&
      normalizeEmail(nextValue) !== pendingConfirmationEmail
    ) {
      clearPendingConfirmation();
    }
    if (
      pendingPasswordResetEmail !== null &&
      normalizeEmail(nextValue) !== pendingPasswordResetEmail
    ) {
      clearPendingPasswordReset();
    }
  };

  const handleSignInSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isForgotPassword || !signInReady || isSubmitting) {
      return;
    }

    if (initialAccountType === "staff") {
      clearPersonaSignupIntentForEmail(signInEmail);
    }

    const result = await submitSignIn({
      email: signInEmail,
      password: signInPassword,
    });
    if (
      result?.status === "next_step" &&
      result.nextStep === "CONFIRM_SIGN_UP"
    ) {
      activatePendingConfirmation(signInEmail);
    } else if (
      result?.status === "next_step" &&
      result.nextStep === "RESET_PASSWORD"
    ) {
      activatePasswordResetRequest();
    }
  };

  const handleSignUpSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!signUpReady || isSubmitting) {
      return;
    }

    const result = await submitSignUp({
      firstName,
      lastName,
      email: signUpEmail,
      password: signUpPassword,
      accountType,
    });

    if (!result) {
      return;
    }

    persistPersonaSignupIntent({
      email: signUpEmail,
      requestedPersona: accountType,
      requestedAccountType: accountType,
      staffSignupCode: resolveStaffSignupCodeForIntent(),
      schoolId: inviteSchoolId,
    });
    setPostSignupPersonaType(accountType);

    const normalizedSignUpEmail = signUpEmail.trim();
    setSignInEmail(normalizedSignUpEmail);
    setSignInPassword("");
    clearPendingPasswordReset();
    setIsForgotPassword(false);
    if (result.status === "confirm_sign_up") {
      activatePendingConfirmation(normalizedSignUpEmail);
    } else {
      clearPendingConfirmation();
    }
    onModeChange("signin");
  };

  const handleResetPassword = async () => {
    if (!forgotEmailValid || isSubmitting) {
      return;
    }

    const result = await submitPasswordReset({ email: signInEmail });
    if (!result) {
      return;
    }

    activatePendingPasswordReset(signInEmail);
  };

  const handleConfirmPasswordReset = async () => {
    if (
      !passwordResetConfirmReady ||
      isSubmitting ||
      !hasPendingPasswordReset
    ) {
      return;
    }

    const result = await submitConfirmPasswordReset({
      email: signInEmail,
      code: passwordResetCode,
      newPassword,
    });
    if (!result) {
      return;
    }

    clearPendingPasswordReset();
    setIsForgotPassword(false);
    setSignInPassword("");
  };

  const handleResendPasswordResetCode = async () => {
    if (
      !isValidEmail(signInEmail) ||
      isSubmitting ||
      !hasPendingPasswordReset
    ) {
      return;
    }
    const result = await submitPasswordReset({ email: signInEmail });
    if (!result) {
      return;
    }
    setPendingPasswordResetEmail(normalizeEmail(signInEmail));
  };

  const handleConfirmSignUp = async () => {
    if (!confirmationReady || isSubmitting || !hasPendingConfirmation) {
      return;
    }
    const result = await submitConfirmSignUp({
      email: signInEmail,
      code: verificationCode,
    });
    if (!result) {
      return;
    }
    clearPendingConfirmation();
  };

  const handleResendSignUpCode = async () => {
    if (!isValidEmail(signInEmail) || isSubmitting || !hasPendingConfirmation) {
      return;
    }
    await submitResendSignUpCode({
      email: signInEmail,
    });
  };

  const handleGoogleSignIn = async () => {
    if (!enableGoogle || isSubmitting) {
      return;
    }

    if (googleProvisioningIntent) {
      persistPersonaSignupIntent(googleProvisioningIntent);
    } else if (mode === "signup") {
      persistPersonaSignupIntent({
        email: signUpEmail,
        requestedPersona: accountType,
        requestedAccountType: accountType,
        staffSignupCode: resolveStaffSignupCodeForIntent(),
        schoolId: inviteSchoolId,
      });
    }

    await submitFederatedSignIn({
      provider: "google",
    });
  };

  const handleLocalPersonaSignIn = async () => {
    if (!allowPersonaTokenMint || isSubmitting) {
      return;
    }
    clearMessages();
    setDevPersonaError(null);

    if (!federatedPersona) {
      setDevPersonaError("Select an impersonation persona first.");
      return;
    }
    const personaEmail = resolveLocalPersonaEmail(federatedPersona);
    if (!personaEmail) {
      setDevPersonaError(
        `No local Cognito test account is configured for "${federatedPersona}".`,
      );
      return;
    }
    const { password } = resolveLocalPersonaSignInPassword({
      manualPassword: devPersonaPassword,
      configuredPassword: configuredPersonaPassword,
      devDefaultPassword: devDefaultPersonaPassword,
      skipPassword: skipPersonaPassword,
    });
    if (!password) {
      setDevPersonaError("Enter the shared test password to continue.");
      return;
    }

    clearStaleCognitoTokenFamilies();
    clearLegacyPersonaStorage();

    await submitSignIn({
      email: personaEmail,
      password,
    });
  };

  const isSignIn = mode === "signin";

  return (
    <div
      className="min-h-screen bg-background-light flex items-stretch"
      data-extracted-from="owned:ystnj/public/auth-root"
    >
      <main
        className="flex w-full min-h-screen"
        data-extracted-from="owned:ystnj/public/auth-main"
      >
        <div
          className={
            hideHeroPane
              ? "w-full flex flex-col bg-white overflow-y-auto"
              : "w-full lg:w-1/2 flex flex-col bg-white overflow-y-auto"
          }
          data-extracted-from="owned:ystnj/public/auth-form-pane"
        >
          {isSignIn ? (
            <div
              className="max-w-[440px] w-full mx-auto px-6 mt-10 lg:mt-40 mb-20"
              data-extracted-from="owned:ystnj/public/auth-signin"
            >
              <div className="mb-10">
                <div
                  className="inline-flex items-center justify-center w-12 h-12 bg-primary rounded-xl mb-8 overflow-hidden bg-center bg-no-repeat"
                  style={
                    effectiveSchoolLogoUrl
                      ? {
                          backgroundImage: `url(${effectiveSchoolLogoUrl})`,
                          backgroundSize: "cover",
                        }
                      : undefined
                  }
                >
                  {!effectiveSchoolLogoUrl ? (
                    <School className="text-jungle font-bold" />
                  ) : null}
                </div>
                {publicSchoolName ? (
                  <p className="mb-2 text-sm font-bold text-jungle">
                    {publicSchoolName}
                  </p>
                ) : null}
                <h1 className="text-3xl font-bold text-irongrey tracking-tight mb-2">
                  Welcome!
                </h1>
                <p className="text-slate-500 font-medium">
                  {isForgotPassword
                    ? "Reset your portal password"
                    : "Access your school portal account"}
                </p>
              </div>

              {postSignupPersonaType === "staff" ? (
                <div
                  data-testid="auth-staff-pending-approval"
                  className="bg-muted border border-border rounded-xl px-4 py-3 mb-4"
                >
                  <p className="text-sm font-semibold text-irongrey">
                    Staff access pending
                  </p>
                  <p className="text-xs text-text-secondary mt-1">
                    You must confirm your email before staff access can be
                    reviewed. We will check your staff code or approved school
                    domain; if neither matches, a school administrator will need
                    to approve staff access before you can enter staff tools.
                  </p>
                </div>
              ) : null}

              <form className="space-y-6" onSubmit={handleSignInSubmit}>
                <div>
                  <label
                    className="block text-sm font-semibold text-irongrey mb-2"
                    htmlFor="signin-email"
                  >
                    Email address
                  </label>
                  <input
                    className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                    id="signin-email"
                    autoComplete="username"
                    placeholder="name@school.edu"
                    type="email"
                    value={signInEmail}
                    onChange={(event) =>
                      handleSignInEmailChange(event.target.value)
                    }
                    disabled={isSubmitting}
                  />
                </div>

                {isForgotPassword ? (
                  hasPendingPasswordReset ? (
                    <div
                      data-testid="auth-password-reset-confirm-panel"
                      className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4"
                    >
                      <p className="text-sm font-semibold text-irongrey">
                        Create a new password
                      </p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Enter the reset code from your email and choose a new
                        password.
                      </p>
                      <label
                        className="mt-4 block text-sm font-semibold text-irongrey mb-2"
                        htmlFor="signin-password-reset-code"
                      >
                        Reset code
                      </label>
                      <input
                        className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                        id="signin-password-reset-code"
                        autoComplete="one-time-code"
                        placeholder="123456"
                        type="text"
                        value={passwordResetCode}
                        onChange={(event) =>
                          setPasswordResetCode(event.target.value)
                        }
                        disabled={isSubmitting}
                      />
                      <label
                        className="mt-4 block text-sm font-semibold text-irongrey mb-2"
                        htmlFor="signin-new-password"
                      >
                        New password
                      </label>
                      <input
                        className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                        id="signin-new-password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        type="password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        disabled={isSubmitting}
                      />
                      <label
                        className="mt-4 block text-sm font-semibold text-irongrey mb-2"
                        htmlFor="signin-confirm-new-password"
                      >
                        Confirm new password
                      </label>
                      <input
                        className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                        id="signin-confirm-new-password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(event) =>
                          setConfirmNewPassword(event.target.value)
                        }
                        disabled={isSubmitting}
                      />
                      {confirmNewPassword.length > 0 && !resetPasswordsMatch ? (
                        <p className="mt-2 text-xs font-medium text-red-700">
                          Passwords must match.
                        </p>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          clearPendingPasswordReset();
                          setIsForgotPassword(false);
                          setShowPasswordResetSupport(false);
                          clearMessages();
                        }}
                        className="text-xs font-bold text-jungle mt-4 hover:underline"
                        disabled={isSubmitting}
                      >
                        Back to sign in
                      </button>
                    </div>
                  ) : (
                    <div
                      data-testid="auth-password-reset-request-panel"
                      className="py-2"
                    >
                      <p className="text-sm text-slate-500 italic leading-relaxed">
                        Not to worry, we&apos;ll send a password reset code to
                        your email.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          clearPendingPasswordReset();
                          setIsForgotPassword(false);
                          setShowPasswordResetSupport(false);
                          clearMessages();
                        }}
                        className="text-xs font-bold text-jungle mt-2 hover:underline"
                        disabled={isSubmitting}
                      >
                        Back to sign in
                      </button>
                    </div>
                  )
                ) : hasPendingConfirmation ? (
                  <div
                    data-testid="auth-confirm-signup-panel"
                    className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4"
                  >
                    <p className="text-sm font-semibold text-irongrey">
                      Verify your account
                    </p>
                    <p className="mt-1 text-xs text-text-secondary">
                      Enter the verification code from your email to finish
                      creating your account.
                    </p>
                    <label
                      className="mt-4 block text-sm font-semibold text-irongrey mb-2"
                      htmlFor="signin-verification-code"
                    >
                      Verification code
                    </label>
                    <input
                      className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                      id="signin-verification-code"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      type="text"
                      value={verificationCode}
                      onChange={(event) =>
                        setVerificationCode(event.target.value)
                      }
                      disabled={isSubmitting}
                    />
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label
                        className="block text-sm font-semibold text-irongrey"
                        htmlFor="signin-password"
                      >
                        Password
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          activatePasswordResetRequest();
                          clearMessages();
                        }}
                        className="text-sm font-semibold text-jungle hover:text-primary transition-colors"
                        disabled={isSubmitting}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <input
                      className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                      id="signin-password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      type="password"
                      value={signInPassword}
                      onChange={(event) =>
                        setSignInPassword(event.target.value)
                      }
                      disabled={isSubmitting}
                    />
                  </div>
                )}

                {errorMessage && (
                  <p
                    className="rounded-xl border border-strawberry/20 bg-strawberry/5 px-4 py-3 text-sm font-semibold text-strawberry"
                    role="alert"
                  >
                    {errorMessage}
                  </p>
                )}
                {infoMessage && (
                  <p className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                    {infoMessage}
                  </p>
                )}

                {isForgotPassword ? (
                  hasPendingPasswordReset ? (
                    <>
                      <button
                        className="w-full bg-gradient-to-r from-jungle to-primary hover:opacity-90 text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/20 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                        type="button"
                        onClick={handleConfirmPasswordReset}
                        disabled={!passwordResetConfirmReady || isSubmitting}
                      >
                        {isSubmitting ? "Updating..." : "Update password"}
                      </button>
                      <button
                        className="w-full border border-slate-200 bg-white hover:bg-slate-50 text-irongrey font-semibold py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        type="button"
                        onClick={handleResendPasswordResetCode}
                        disabled={!isValidEmail(signInEmail) || isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Resend reset code"}
                      </button>
                    </>
                  ) : (
                    <button
                      className="w-full bg-gradient-to-r from-jungle to-primary hover:opacity-90 text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/20 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                      type="button"
                      onClick={handleResetPassword}
                      disabled={!forgotEmailValid || isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send reset code"}
                    </button>
                  )
                ) : hasPendingConfirmation ? (
                  <>
                    <button
                      className="w-full bg-gradient-to-r from-jungle to-primary hover:opacity-90 text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/20 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                      type="button"
                      onClick={handleConfirmSignUp}
                      disabled={!confirmationReady || isSubmitting}
                    >
                      {isSubmitting ? "Confirming..." : "Confirm account"}
                    </button>
                    <button
                      className="w-full border border-slate-200 bg-white hover:bg-slate-50 text-irongrey font-semibold py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      type="button"
                      onClick={handleResendSignUpCode}
                      disabled={!isValidEmail(signInEmail) || isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Resend code"}
                    </button>
                  </>
                ) : (
                  <button
                    className="w-full bg-gradient-to-r from-jungle to-primary hover:opacity-90 text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/20 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                    type="submit"
                    disabled={!signInReady || isSubmitting}
                  >
                    {isSubmitting ? "Signing in..." : "Sign in"}
                  </button>
                )}

                {isForgotPassword ? (
                  <div className="space-y-3">
                    <button
                      type="button"
                      className="rounded text-xs font-semibold text-slate-500 transition-colors hover:text-jungle focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2"
                      aria-expanded={showPasswordResetSupport}
                      aria-controls="auth-password-reset-support"
                      onClick={() =>
                        setShowPasswordResetSupport((current) => !current)
                      }
                    >
                      {showPasswordResetSupport
                        ? "Hide support details"
                        : "Still having trouble?"}
                    </button>
                    {showPasswordResetSupport ? (
                      <div
                        id="auth-password-reset-support"
                        className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3"
                      >
                        <p className="text-xs font-medium leading-5 text-irongrey">
                          Try the reset code first. If you still cannot get into
                          your account, contact Intellio support.
                        </p>
                        <EngineeringSupportBanner compact />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {!isForgotPassword && !hasPendingConfirmation ? (
                  <>
                    <AuthGoogleSection
                      dividerLabel="or continue with"
                      buttonLabel="Sign in with Google"
                      enableGoogle={enableGoogle}
                      isSubmitting={isSubmitting}
                      onGoogleContinue={handleGoogleSignIn}
                      unavailableMessage="Google sign-in is unavailable in this app configuration."
                    />
                    {allowPersonaTokenMint ? (
                      <div
                        className="rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-4 py-3"
                        data-testid="auth-local-persona-mint"
                      >
                        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-800">
                          {import.meta.env.DEV
                            ? "Local Persona Token Mint (Dev)"
                            : "Persona Token Mint (Temporary)"}
                        </p>
                        <p className="mt-1 text-xs text-emerald-700">
                          Uses direct Cognito username/password sign-in to mint
                          real tokens without Google redirect.
                        </p>
                        <label
                          className="mt-3 block text-xs font-semibold text-emerald-900"
                          htmlFor="local-persona"
                        >
                          Local impersonation persona
                        </label>
                        <select
                          id="local-persona"
                          className="mt-1 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-irongrey outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                          value={federatedPersona}
                          onChange={(event) => {
                            setFederatedPersona(
                              event.target.value as FederatedPersona | "",
                            );
                            if (devPersonaError) {
                              setDevPersonaError(null);
                            }
                          }}
                          disabled={isSubmitting}
                        >
                          <option value="">Select local persona</option>
                          {FEDERATED_PERSONA_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {federatedPersona ? (
                          <p className="mt-2 text-xs text-emerald-900">
                            Account:{" "}
                            {selectedLocalPersonaAccountLabel ||
                              `No account mapped for ${federatedPersona}`}
                          </p>
                        ) : null}
                        <label
                          className="mt-3 block text-xs font-semibold text-emerald-900"
                          htmlFor="dev-persona-password"
                        >
                          Shared test password
                        </label>
                        {!shouldShowLocalPersonaPasswordInput ? (
                          <p className="mt-1 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs text-emerald-900">
                            Password requirement bypass is enabled by
                            runtime/environment configuration.
                          </p>
                        ) : (
                          <input
                            id="dev-persona-password"
                            type="password"
                            autoComplete="current-password"
                            className="mt-1 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-irongrey outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                            value={devPersonaPassword}
                            placeholder="Enter shared test password"
                            onChange={(event) => {
                              setDevPersonaPassword(event.target.value);
                              if (devPersonaError) {
                                setDevPersonaError(null);
                              }
                            }}
                            disabled={isSubmitting}
                          />
                        )}
                        <button
                          type="button"
                          className="mt-3 w-full rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                          data-testid="auth-local-persona-submit"
                          onClick={handleLocalPersonaSignIn}
                          disabled={
                            !federatedPersona ||
                            !localPersonaPasswordResolution.password ||
                            isSubmitting
                          }
                        >
                          {isSubmitting
                            ? "Signing in with Cognito..."
                            : "Sign in with selected test persona"}
                        </button>
                        {devPersonaError ? (
                          <p className="mt-2 text-xs font-medium text-red-700">
                            {devPersonaError}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </form>

              <p className="text-center text-sm text-slate-400 mt-10">
                Don&apos;t have an account?{" "}
                <button
                  onClick={() => {
                    clearPendingConfirmation();
                    clearPendingPasswordReset();
                    setIsForgotPassword(false);
                    clearMessages();
                    onModeChange("signup");
                  }}
                  className="text-jungle font-semibold hover:underline"
                  type="button"
                  disabled={isSubmitting}
                >
                  Sign up
                </button>
              </p>
            </div>
          ) : (
            <div
              className="max-w-[440px] w-full mx-auto px-6 mb-20 mt-10"
              data-extracted-from="owned:ystnj/public/auth-signup"
            >
              <div className="mb-8">
                <div
                  className="inline-flex items-center justify-center w-12 h-12 bg-primary rounded-xl mb-6 overflow-hidden bg-center bg-no-repeat"
                  style={
                    effectiveSchoolLogoUrl
                      ? {
                          backgroundImage: `url(${effectiveSchoolLogoUrl})`,
                          backgroundSize: "cover",
                        }
                      : undefined
                  }
                >
                  {!effectiveSchoolLogoUrl ? (
                    <School className="text-jungle font-bold" />
                  ) : null}
                </div>
                {publicSchoolName ? (
                  <p className="mb-2 text-sm font-bold text-jungle">
                    {publicSchoolName}
                  </p>
                ) : null}
                <h1 className="text-3xl font-bold text-irongrey tracking-tight mb-2">
                  Create an account
                </h1>
                <p className="text-slate-500 font-medium">
                  Join the future of school management
                </p>
                {inviteSchoolId ? (
                  <p className="mt-2 text-sm text-jungle">
                    This invite link is already attached to your school.
                    Continue with School Staff to request faculty access.
                  </p>
                ) : null}
              </div>

              <form className="space-y-5" onSubmit={handleSignUpSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-sm font-semibold text-irongrey mb-2"
                      htmlFor="first-name"
                    >
                      First Name
                    </label>
                    <input
                      className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                      id="first-name"
                      autoComplete="given-name"
                      placeholder="John"
                      type="text"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm font-semibold text-irongrey mb-2"
                      htmlFor="last-name"
                    >
                      Last Name
                    </label>
                    <input
                      className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                      id="last-name"
                      autoComplete="family-name"
                      placeholder="Doe"
                      type="text"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block text-sm font-semibold text-irongrey mb-2"
                    htmlFor="signup-email"
                  >
                    Email address
                  </label>
                  <input
                    className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                    id="signup-email"
                    autoComplete="username"
                    placeholder="name@school.edu"
                    type="email"
                    value={signUpEmail}
                    onChange={(event) => setSignUpEmail(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-semibold text-irongrey mb-2"
                    htmlFor="signup-password"
                  >
                    Password
                  </label>
                  <input
                    className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                    id="signup-password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    type="password"
                    value={signUpPassword}
                    onChange={(event) => setSignUpPassword(event.target.value)}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="pt-2">
                  <div className="flex p-1 bg-slate-100 rounded-xl h-[60px] relative items-stretch">
                    <div className="flex-1 relative flex">
                      <input
                        checked={accountType === "staff"}
                        className="hidden peer"
                        id="toggle-school"
                        name="account-type"
                        type="radio"
                        onChange={() => {
                          setAccountType("staff");
                          if (allowPersonaTokenMint) {
                            setFederatedPersona("staff");
                          }
                        }}
                        disabled={isSubmitting}
                      />
                      <label
                        className={`flex items-center justify-center w-full h-full rounded-lg cursor-pointer text-sm font-bold text-slate-500 transition-all peer-checked:bg-white peer-checked:text-jungle peer-checked:shadow-sm${accountType === "staff" ? " ring-2 ring-jungle/30" : ""}`}
                        htmlFor="toggle-school"
                      >
                        School Staff
                      </label>
                    </div>
                    <div className="flex-1 relative flex">
                      <input
                        checked={accountType === "parent"}
                        className="hidden peer"
                        id="toggle-parent"
                        name="account-type"
                        type="radio"
                        onChange={() => {
                          setAccountType("parent");
                          setStaffSignupCode("");
                          if (allowPersonaTokenMint) {
                            setFederatedPersona("parent");
                          }
                        }}
                        disabled={isSubmitting}
                      />
                      <label
                        className="flex items-center justify-center w-full h-full rounded-lg cursor-pointer text-sm font-bold text-slate-500 transition-all peer-checked:bg-white peer-checked:text-jungle peer-checked:shadow-sm"
                        htmlFor="toggle-parent"
                      >
                        Parent
                      </label>
                    </div>
                  </div>
                </div>

                {accountType === "staff" ? (
                  <div>
                    <label
                      className="block text-sm font-semibold text-irongrey mb-2"
                      htmlFor="signup-staff-code"
                    >
                      Staff signup code (optional)
                    </label>
                    <input
                      className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-irongrey focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 outline-none"
                      id="signup-staff-code"
                      autoComplete="off"
                      placeholder="Enter staff invite code from admin"
                      type="text"
                      value={staffSignupCode}
                      onChange={(event) =>
                        setStaffSignupCode(event.target.value)
                      }
                      disabled={isSubmitting}
                    />
                    <p className="mt-2 text-xs text-text-secondary">
                      Codes come from an existing school administrator. Your
                      emailed verification code only verifies your account;
                      staff access still requires this code, an approved school
                      email domain, or administrator approval.
                    </p>
                    <p className="mt-1 text-xs text-text-secondary">
                      If your school email domain is approved, you may be
                      auto-approved without a code. The first admin signs in
                      with an existing admin or test-admin account, then creates
                      codes in Settings &gt; Users &gt; Staff Signup Codes.
                    </p>
                  </div>
                ) : null}

                {errorMessage && (
                  <p
                    className="rounded-xl border border-strawberry/20 bg-strawberry/5 px-4 py-3 text-sm font-semibold text-strawberry"
                    role="alert"
                  >
                    {errorMessage}
                  </p>
                )}
                {infoMessage && (
                  <p className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                    {infoMessage}
                  </p>
                )}

                <button
                  className="w-full bg-gradient-to-r from-jungle to-primary hover:opacity-90 text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/20 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={!signUpReady || isSubmitting}
                >
                  {isSubmitting ? "Creating account..." : "Sign up"}
                </button>

                <AuthGoogleSection
                  dividerLabel="or sign up with"
                  buttonLabel="Sign up with Google"
                  enableGoogle={enableGoogle}
                  isSubmitting={isSubmitting}
                  onGoogleContinue={handleGoogleSignIn}
                  unavailableMessage="Google sign-up is unavailable in this app configuration."
                />
              </form>

              <p className="text-center text-sm text-slate-400 mt-8">
                Already have an account?{" "}
                <button
                  onClick={() => {
                    clearPendingConfirmation();
                    clearPendingPasswordReset();
                    clearMessages();
                    onModeChange("signin");
                  }}
                  className="text-jungle font-semibold hover:underline"
                  type="button"
                  disabled={isSubmitting}
                >
                  Sign in
                </button>
              </p>
            </div>
          )}
        </div>

        {!hideHeroPane && (
          <div
            className="hidden lg:flex w-1/2 relative overflow-hidden bg-gradient-to-br from-[#1a332a] to-jungle p-12 flex-col justify-between items-start"
            data-extracted-from="owned:ystnj/public/auth-hero-pane"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -top-[20%] -right-[10%] h-[80%] w-[80%] blur-[60px] bg-[radial-gradient(circle,rgba(40,226,167,0.15)_0%,transparent_70%)]"
            />
            <div className="relative z-10 pt-12">
              <h2 className="text-white text-6xl xl:text-7xl font-heading leading-tight">
                Your school, <br />
                <span className="italic font-light">made easy</span>
              </h2>
            </div>

            <div className="relative z-10 w-full max-w-lg mx-auto">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 rounded-full bg-aquamarine/20 flex items-center justify-center">
                    <User className="text-aquamarine text-3xl" size={32} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Noah Kaplan</p>
                    <p
                      className="text-primary/80 text-sm"
                      style={{ color: "#7fffd48c" }}
                    >
                      5th Grade
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">
                      Documents
                    </p>
                    <p className="text-white text-2xl font-bold">4/7</p>
                  </div>
                  <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">
                      Status
                    </p>
                    <p className="text-white text-2xl font-bold">Registered</p>
                  </div>
                </div>
                <div className="mt-6 bg-white/5 rounded-2xl p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-white/60 text-xs uppercase tracking-wider">
                      Registration Progress
                    </p>
                    <p className="text-primary text-xs font-bold">75%</p>
                  </div>
                  <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden">
                    <div className="bg-aquamarine h-full w-3/4" />
                  </div>
                </div>
              </div>
              <div
                className="absolute -right-6 bg-primary text-white p-4 rounded-2xl shadow-xl flex items-center gap-3"
                style={{ bottom: "-34px" }}
              >
                <BadgeCheck className="w-6 h-6" />
                <span className="font-bold text-sm">Approved</span>
              </div>
            </div>

            <div className="relative z-10">
              <p className="text-white/50 text-sm">
                © 2026 Intellio, Inc. All rights reserved.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
